import { Stack, Card, Box, Typography, Chip, Tooltip, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useList } from "src/hooks/useFetch.hook";
import { BaseTable } from "../BaseTable/BaseTable";
import { useSelector, useDispatch } from "react-redux";
import { vehicleListColumns } from "./Vehicles.constants";
import Page from "../Page";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";
import VehicleDetailForm from "./VehicleDetailForm";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { Icon } from '@iconify/react';
import refreshFill from "@iconify/icons-eva/refresh-fill";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.primary.light}20`,
    border: `1px solid ${theme.palette.primary.light}60`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

export const VehicleList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasCreateAccess = useAccess(FEATURE.VEHICLE, "C");
  const isReadOnly = useAccess(FEATURE.VEHICLE, "R");
  const { data: vehicles, isFetching, refetch } = useList("vehicles");
  const { open, data } = useSelector(({ dialog }) => dialog);
  const handleClose = () => dispatch(dialogSlice.actions.setDialog(false));
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState('');

  const rowOrCellClickRedirect = ({ id }) => {
    if(isReadOnly) return;
    navigate(`/vehicles/${id}`);
  };

  useEffect(() => {
    if (!isFetching && vehicles) {
      // Ensure each row has an id property by mapping vehicle_id to id
      const dataWithIds = vehicles.map(vehicle => ({
        id: vehicle.vehicle_id, // Add id property
        ...vehicle              // Spread all other properties
      }));
      setTableData(dataWithIds);
    }
  }, [isFetching, vehicles]);

  const handleSearching = (event) => {
    const dataToSearchFrom = vehicles;
    const searchTerm = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchTerm);
    
    if (searchTerm) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "make",
            "model",
            "registration_number",
            "vehicle_type",
            "assigned_driver_name",
            "vin",
            "battery_mac_1",
            "battery_mac_2",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) => 
              value ? value.toString().toLowerCase() : ""
            );
          
          // MACs are stored without separators, so "9c:f6:dd" also matches "9cf6dd...".
          const macTerm = /[:\-.]/.test(searchTerm) ? searchTerm.replace(/[^0-9a-f]/g, "") : "";
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchTerm) || (macTerm && value.includes(macTerm))
          );
          
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, {
              id: currentObj.vehicle_id, // Ensure id is included
              ...currentObj
            }];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
    } else {
      // Ensure id is included when resetting to original data
      setTableData(vehicles?.map(vehicle => ({
        id: vehicle.vehicle_id,
        ...vehicle
      })) ?? []);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const openVehicleForm = () => {
    dispatch(dialogSlice.actions.setDialog({
      open: true,
      data: null, // For new vehicle
      component: <VehicleDetailForm onClose={handleClose} />
    }));
  };

  return (
    <Page
      {...(hasCreateAccess && { path: `/vehicles/new` })}
      actionButtonText="Add New Vehicle"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            EV Vehicles
          </Typography> */}
           <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Manage Vehicles
              </Typography>
          {searchValue && (
            <Chip
              label={`${tableData?.length || 0} results`}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      </Stack>
      
      <StyledListCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CommonSearchbar
              placeholder="Search vehicles"
              searchFunc={(e) => handleSearching(e)}
            />
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isFetching}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <BaseTable
          rows={tableData ?? []}
          isLoading={isFetching}
          columns={vehicleListColumns(isReadOnly)}
          showExportCsvBtn={false}
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          pageSize={10}
          isReadOnly={isReadOnly}
          getRowId={(row) => row.vehicle_id} // Add this prop to specify the ID field
          height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-main': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-row': {
              height: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              height: '52px !important',
            }
          }}
        />
      </StyledListCard>
    </Page>
  );
};